import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, Lock, User as UserIcon, ArrowLeft, ShieldAlert } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "A apărut o eroare. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Dacă admin-ul este deja logat, mergem direct la dashboard (în useEffect, nu în render)
  useEffect(() => {
    if (isAuthenticated) {
      const dest = location.state?.from || "/admin/dashboard";
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      const dest = location.state?.from || "/admin/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-4 gap-2 text-white hover:bg-white/10" data-testid="back-to-form">
            <ArrowLeft className="w-4 h-4" />
            Înapoi la formular
          </Button>
        </Link>

        <Card className="shadow-2xl border-0">
          <CardHeader className="space-y-2 text-center pb-4">
            <div className="w-14 h-14 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <ShieldAlert className="w-7 h-7 text-primary" />
            </div>
            <CardTitle className="text-2xl">Acces Administrator</CardTitle>
            <p className="text-sm text-muted-foreground">
              Introdu credențialele pentru a accesa panoul de administrare
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-sm font-medium">
                  <UserIcon className="w-4 h-4 inline mr-1.5 mb-0.5" />
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Introdu username-ul"
                  data-testid="login-username"
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium">
                  <Lock className="w-4 h-4 inline mr-1.5 mb-0.5" />
                  Parolă
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                />
              </div>

              {error && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md p-3" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="login-submit">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Se autentifică...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Autentificare
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
